import React, { useContext, useEffect, useState } from "react";
import "./PromoCodes.css";
import { SidebarContext } from "../../context/SidebarContext";
import SectionMenuLeft from "../../components/SectionMenuLeft/SectionMenuLeft";
import { ToastContainer, toast } from "react-toastify";
import { config } from "../../config";
import ReactTable from "../../components/ReactTable/ReactTable.jsx";
import { useMemo } from "react";
import { LuArrowLeft } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

function PromoCodes() {
  const token = localStorage.getItem("adminToken");
  const { isSidebar } = useContext(SidebarContext);
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isPromoSaving, setIsPromoSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [editingCode, setEditingCode] = useState(null);
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    discount: "",
    maxDiscount: "",
    validFrom: "",
    validTo: "",
    isActive: true,
  });
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const fetchUsers = async () => {
    try {
      const response = await fetch(`${config.backEndBaseUrl}user/get`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Failed to fetch users");
      }

      setUsers(
        (data.users || []).map((u, i) => ({
          id: u.uid || u.id || u._id, //  ALWAYS uid first
          name: u.userName || "",
          email: u.email || "",
          ...u,
        })),
      );
    } catch (error) {
      toast.error(error.message);
    }
  };
  const handleSubmit = async () => {
    setIsPromoSaving(true);
    const today = new Date().toISOString().split("T")[0];

    if (Number(formData.discount) < 0 || Number(formData.maxDiscount) < 0) {
      toast.error("Discount and max discount cannot be negative");
      setIsPromoSaving(false);
      return;
    }

    if (formData.validFrom < today) {
      toast.error("Valid From cannot be a past date");
      setIsPromoSaving(false);
      return;
    }

    if (formData.validTo < today) {
      toast.error("Valid To cannot be a past date");
      setIsPromoSaving(false);
      return;
    }

    if (formData.validTo < formData.validFrom) {
      toast.error("Valid To cannot be before Valid From");
      setIsPromoSaving(false);
      return;
    }
    try {
      const payload = {
        ...formData,
        discount: Number(formData.discount),
        maxDiscount: Number(formData.maxDiscount),
        userIds: selectedUsers,
      };

      const response = await fetch(`${config.backEndBaseUrl}promocode/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "Failed to add promo");
      }

      toast.success("Promo added successfully");

      setShowModal(false);
      fetchPromoCodes(); // refresh table

      setFormData({
        code: "",
        description: "",
        discount: "",
        maxDiscount: "",
        validFrom: "",
        validTo: "",
        isActive: true,
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsPromoSaving(false);
    }
  };
  /* ---------------- FETCH PROMO CODES ---------------- */
  const fetchPromoCodes = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${config.backEndBaseUrl}promocode/get`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Failed to fetch promo codes");
      }

      setPromoCodes(data.data || []);
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };
  const handleUpdate = async () => {
    setIsPromoSaving(true);

    if (Number(formData.discount) < 0 || Number(formData.maxDiscount) < 0) {
      toast.error("Discount and max discount cannot be negative");
      setIsPromoSaving(false);
      return;
    }
    if (formData.validTo < formData.validFrom) {
      toast.error("Valid To cannot be before Valid From");
      setIsPromoSaving(false);
      return;
    }

    try {
      const payload = {
        originalCode: editingCode,
        code: formData.code,
        description: formData.description,
        discount: Number(formData.discount),
        maxDiscount: Number(formData.maxDiscount),
        validFrom: formData.validFrom,
        validTo: formData.validTo,
        userIds: selectedUsers.length ? selectedUsers : [],
        isActive: formData.isActive,
      };

      const response = await fetch(`${config.backEndBaseUrl}promocode/assign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "Failed to update promo");
      }

      toast.success("Promo updated successfully");

      setShowModal(false);
      setIsEditMode(false);
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsPromoSaving(false);
    }
  };
  const [search, setSearch] = useState("");
  const normalizeId = (id) => String(id);
  const toggleUser = (id) => {
    const normalized = normalizeId(id);

    setSelectedUsers((prev) =>
      prev.includes(normalized)
        ? prev.filter((u) => u !== normalized)
        : [...prev, normalized],
    );
  };
  const removeUser = (id) => {
    setSelectedUsers((prev) => prev.filter((u) => u !== id));
  };
  useEffect(() => {
    fetchPromoCodes();
    fetchUsers();
  }, []);

  const handleEdit = (promo) => {
    setIsEditMode(true);
    setShowModal(true);
    setEditingCode(promo.code);

    setFormData({
      code: promo.code,
      description: promo.description || "",
      discount: promo.discount || "",
      maxDiscount: promo.maxDiscount || "",
      validFrom: promo.validFrom?._seconds
        ? new Date(promo.validFrom._seconds * 1000).toISOString().split("T")[0]
        : "",
      validTo: promo.validTo?._seconds
        ? new Date(promo.validTo._seconds * 1000).toISOString().split("T")[0]
        : "",
      isActive: promo.isActive ?? true,
    });

    setSelectedUsers(promo.userIds || []);
  };
  console.log("Selected Users:", selectedUsers);
  console.log("Users:", users);
  /* ---------------- TABLE ---------------- */
  const columns = useMemo(
    () => [
      {
        header: "Code",
        accessorKey: "code",
      },
      {
        header: "Description",
        accessorKey: "description",
      },
      {
        header: "Discount",
        accessorKey: "discount",
        Cell: ({ cell }) => `${cell.row.original.discount}%`,
      },
      {
        header: "Max Discount",
        accessorKey: "maxDiscount",
        Cell: ({ cell }) => `₹${cell.row.original.maxDiscount}`,
      },
      {
        header: "Valid From",
        accessorKey: "validFrom",
        Cell: ({ cell }) => {
          const value = cell.row.original.validFrom;
          if (!value?._seconds) return "-";

          return new Date(value._seconds * 1000).toLocaleDateString("en-IN");
        },
      },
      {
        header: "Valid To",
        accessorKey: "validTo",
        Cell: ({ cell }) => {
          const value = cell.row.original.validTo;
          if (!value?._seconds) return "-";

          return new Date(value._seconds * 1000).toLocaleDateString("en-IN");
        },
      },
      {
        header: "Users",
        accessorKey: "users",
        Cell: ({ cell }) => {
          const users = cell.row.original.users || [];

          if (!users.length) return "-";

          return (
            <div>
              {users.map((u, i) => (
                <div key={i}>{u.name}</div>
              ))}
            </div>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "isActive",
        Cell: ({ cell }) => (
          <span
            className={`badge ${cell.row.original.isActive ? "bg-success" : "bg-danger"}`}
          >
            {cell.row.original.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        header: "Action",
        Cell: ({ row }) => (
          <button
            className="btn btn-sm btn-success"
            onClick={() => handleEdit(row.original)}
          >
            Edit
          </button>
        ),
      },
    ],
    [],
  );

  /* ---------------- SKELETON ---------------- */
  const TableSkeleton = ({ rows = 6 }) => (
    <div className="table-skeleton">
      {[...Array(rows)].map((_, i) => (
        <div className="skeleton-row" key={i}>
          <div className="skeleton-cell" />
          <div className="skeleton-cell" />
          <div className="skeleton-cell" />
          <div className="skeleton-cell" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="body">
      <div id="wrapper">
        <div id="page">
          <div className="layout-wrap">
            <SectionMenuLeft />

            <div className="section-content-right">
              <div
                className="main-content"
                style={{ paddingLeft: isSidebar ? "280px" : 0 }}
              >
                <div className="main-content-inner">
                  <ToastContainer />

                  <div className="wg-box">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center justify-between mb-12 flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                          <button
                            className="back-btn flex items-center gap-2"
                            onClick={() => navigate("/")}
                          >
                            <LuArrowLeft size={16} />
                            {/* <span>Back</span> */}
                          </button>

                          <h5 className="fare-heading">Promo Codes</h5>
                        </div>
                      </div>
                      <button
                        className="btn btn-success"
                        onClick={() => {
                          setShowModal(true);
                          setIsEditMode(false);
                          setEditingCode(null);

                          setFormData({
                            code: "",
                            description: "",
                            discount: "",
                            maxDiscount: "",
                            validFrom: "",
                            validTo: "",
                            isActive: true,
                          });

                          setSelectedUsers([]);
                        }}
                      >
                        Add Promo Code +
                      </button>
                    </div>

                    {/* Table */}
                    {loading ? (
                      <TableSkeleton />
                    ) : (
                      <ReactTable columns={columns} data={promoCodes} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h5>{isEditMode ? "Update Promo Code" : "Add Promo Code"}</h5>

            <div className="form-grid">
              <div className="form-group">
                <label>Code</label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Discount (%)</label>
                <input
                  type="number"
                  name="discount"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.discount}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Max Discount</label>
                <input
                  type="number"
                  name="maxDiscount"
                  min="0"
                  step="0.01"
                  value={formData.maxDiscount}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Valid From</label>
                <input
                  type="date"
                  name="validFrom"
                  min={today}
                  value={formData.validFrom}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Valid To</label>
                <input
                  type="date"
                  name="validTo"
                  min={formData.validFrom || today}
                  value={formData.validTo}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Status</label>

                <div
                  className={`toggle ${formData.isActive ? "active" : ""}`}
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      isActive: !prev.isActive,
                    }))
                  }
                >
                  <div className="toggle-circle" />
                </div>
              </div>
              <div className="form-group full">
                <label>Assign Users</label>

                <input
                  type="text"
                  placeholder="Search users..."
                  className="user-search"
                  onChange={(e) => setSearch(e.target.value)}
                />

                <div className="user-list">
                  {users
                    .filter((user) =>
                      (user.name || user.email || "")
                        .toLowerCase()
                        .includes(search.toLowerCase()),
                    )
                    .map((user) => (
                      <div
                        key={user.id}
                        className={`user-item ${
                          selectedUsers.includes(String(user.id))
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => toggleUser(user.id)}
                      >
                        {user.name || user.email}
                      </div>
                    ))}
                </div>

                {/* Selected */}
                <div className="selected-users">
                  {selectedUsers.map((id) => {
                    const user = users.find((u) => u.id === id);
                    return (
                      <span key={id} className="chip">
                        {user?.name || user?.email}
                        <span onClick={() => removeUser(id)}>×</span>
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-dark"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>

              <button
                className="btn btn-success"
                onClick={isEditMode ? handleUpdate : handleSubmit}
                disabled={isPromoSaving}
              >
                {isPromoSaving ? "Submiting...." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PromoCodes;
